// Sends a product/support event to Tawk.to.
//
// This is the single, canonical way to emit an event from anywhere in the app
// (components — Options API or <script setup> — the store, or other services).
// It is a leaf module with no app-internal deps, so it can be imported from
// anywhere without risking a load-time import cycle.
//
// Safety contract — sending an event must NEVER affect the operation that
// triggered it:
//   - window.Tawk_API always exists (main.js installs a no-op stub when
//     third-party scripts are disabled), so reading it is safe.
//   - addEvent only exists on Tawk_API once the widget has loaded; until then
//     events are buffered (see below) rather than thrown away or throwing.
//   - any error from Tawk is swallowed (but logged — see below).
// => Always call trackEvent AFTER the underlying operation has succeeded, and
//    never wrap it in your own try/catch — it cannot throw.
//
// Metadata rules (https://developer.tawk.to/jsapi/ — addEvent / setAttributes):
//   - Keys must be alphanumeric or '-' (dash) and are lower-cased by Tawk; a
//     '_' or other punctuation returns CONTAINS_INVALID_KEY and drops the
//     WHOLE event. So we lower-case and map '_' -> '-': callers keep writing
//     natural `meat_id` and it reaches Tawk as `meat-id`.
//   - Values must be non-empty strings <= 255 chars; we stringify, drop
//     empties, and clamp length to avoid CONTAINS_INVALID_VALUE.
//
// The Tawk widget script loads asynchronously and the Vue plugin installs no
// queueing stub, so addEvent does not exist for the first few hundred ms
// (longer on mobile). Events fired in that window are buffered and flushed once
// the widget fires onLoad — otherwise every early event is silently lost.
//
// See frontend/AGENTS.md for naming and privacy rules; call sites define the event inventory.

import posthog from "posthog-js";

const posthogConfigured = Boolean(
    import.meta.env.VITE_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_POSTHOG_HOST
);
const posthogPropertyKeys = new Set([
    "action",
    "audit_type",
    "count",
    "element_count",
    "file_count",
    "file_type",
    "form_count",
    "location_count",
    "location_type",
    "quantity",
    "reason",
    "step",
    "style",
    "template_used",
    "version",
    "wastage_type"
]);

function posthogProperties(metadata) {
    return Object.fromEntries(
        Object.entries(metadata).filter(([key, value]) =>
            posthogPropertyKeys.has(key) && value !== undefined && value !== null
        )
    );
}

const MAX_QUEUE = 100;
const queue = [];
let flushHookInstalled = false;

// Returns the live Tawk_API only once addEvent is a real function (i.e. the
// remote widget has loaded), otherwise null.
function readyTawk() {
    const tawk = typeof window !== "undefined" ? window.Tawk_API : null;
    return tawk && typeof tawk.addEvent === "function" ? tawk : null;
}

function send(tawk, name, metadata) {
    tawk.addEvent(name, metadata, (err) => {
        // Best-effort: never surface to the caller, but don't silently rot — a
        // rejection (CONTAINS_INVALID_KEY, CONTAINS_INVALID_VALUE,
        // SESSION_EXPIRED, ATTRIBUTE_LIMIT_EXCEEDED, …) should be visible.
        if (err) console.warn("Tawk addEvent rejected:", name, err);
    });
}

function flushQueue() {
    const tawk = readyTawk();
    if (!tawk) return;
    while (queue.length) {
        const { name, metadata } = queue.shift();
        try {
            send(tawk, name, metadata);
        } catch {
            // best-effort
        }
    }
}

// Chain onto Tawk_API.onLoad (preserving any existing handler, e.g. App.vue's
// secure-mode login) so the buffer drains the moment the widget is ready.
function ensureFlushOnLoad() {
    if (flushHookInstalled || typeof window === "undefined" || !window.Tawk_API) {
        return;
    }
    flushHookInstalled = true;
    const previousOnLoad = window.Tawk_API.onLoad;
    window.Tawk_API.onLoad = () => {
        if (typeof previousOnLoad === "function") previousOnLoad();
        flushQueue();
    };
}

export function trackEvent(name, metadata = {}) {
    try {
        // PostHog identity is set centrally when the authenticated user becomes active.
        // Forward only product metrics with an explicit safe-property contract; event
        // payloads must never include names, emails, notes, or other user-entered data.
        if (posthogConfigured) posthog.capture(name, posthogProperties(metadata));

        const clean = {};
        for (const [key, value] of Object.entries(metadata)) {
            if (value === undefined || value === null) continue;
            const safeKey = key
                .toLowerCase()
                .replace(/_/g, "-")
                .replace(/[^a-z0-9-]/g, "");
            if (!safeKey) continue;
            let v = String(value);
            if (v.length === 0) continue;
            if (v.length > 255) v = v.slice(0, 255);
            clean[safeKey] = v;
        }

        const tawk = readyTawk();
        if (tawk) {
            send(tawk, name, clean);
            return;
        }

        // Widget not loaded yet — buffer and flush on onLoad. Bounded so a
        // never-loading widget (e.g. third-party scripts disabled in dev)
        // can't grow the queue without limit.
        if (queue.length >= MAX_QUEUE) queue.shift();
        queue.push({ name, metadata: clean });
        ensureFlushOnLoad();
    } catch {
        // Tracking is best-effort: never surface a failure to the caller.
    }
}
